import type { RefObject } from 'react';
import { useTracking } from 'react-tracking';

import { Button } from '@simplywallst/ui-core/next';
import useUser from '@/hooks/useUser';
import GuestRegistrationModal from '@components/GuestRegistrationModal';
import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';
import { FairValueTooltip } from '@/pages/Narrative/components/FairValueTooltip';
import { NarrativePanelRegister } from '@/components/GuestRegistrationModal/components/NarrativePanelRegister';
import UserOutline from '@simplywallst/ui-core/icons/UserOutline';
import { useNarrativeTogglePrimary } from '../../hooks/useNarrativeTogglePrimary';

type Props = {
  companyId: string;
  narrativeId: string;
  isPrimary: boolean;
  containerRef: RefObject<HTMLDivElement>;
  primaryNarrativeCount?: number;
};

export const SetFairValue = ({
  companyId,
  narrativeId,
  isPrimary,
  containerRef,
  primaryNarrativeCount,
}: Props) => {
  const {
    isSaving,
    setGuestRegistrationModal,
    togglePrimary,
    isGuestRegistrationModalOpen,
    triggerRef,
  } = useNarrativeTogglePrimary({
    companyId,
    narrativeId,
    valuationType: 'USER_DEFINED',
    isPrimary,
  });

  const { data: user, mutate: mutateUser } = useUser();
  const { trackEvent } = useTracking();
  const showSetFairValueTooltip = !user.narrative?.hasDismissedFairValueTooltip;

  const handleOnDismissTooltip = () => {
    trackEvent({
      subject: 'set_fair_value_tooltip',
      action: 'dismiss',
      companyId,
      narrativeId,
    });

    mutateUser({
      settings: {
        narrative: {
          has_dismissed_fair_value_tooltip: true,
        },
      },
    });
  };

  if (!user.loggedIn) {
    return (
      <>
        <Button
          size="medium"
          variant="primary"
          onClick={() => setGuestRegistrationModal(true)}
        >
          <div className="flex items-center gap-0.5">
            <div className="flex items-center gap-2 border-r border-solid pr-3">
              <UserOutline />
              {primaryNarrativeCount || 0}
            </div>
            <div className="pl-2">Set Fair Value</div>
          </div>
        </Button>
        {isGuestRegistrationModalOpen && (
          <GuestRegistrationModal
            canClose
            onClose={() => setGuestRegistrationModal(false)}
            removeWindowScroll
            onLoginSuccess={() => setGuestRegistrationModal(false)}
            onRegisterSuccess={() => setGuestRegistrationModal(false)}
            SplashPanel={{
              Component: NarrativePanelRegister,
              isDark: true,
            }}
          />
        )}
      </>
    );
  }

  const handleOnClick = async () => {
    await togglePrimary();
    handleOnDismissTooltip();
  };

  const renderButtonText = () => {
    if (isSaving) {
      return <div className="pl-2">Saving</div>;
    }

    return isPrimary ? (
      <>
        <CheckSolid />
        &nbsp;Fair Value
      </>
    ) : (
      <div className="pl-2">Set Fair Value</div>
    );
  };

  return (
    <FairValueTooltip
      show={showSetFairValueTooltip && !isPrimary}
      onClick={handleOnDismissTooltip}
      containerRef={containerRef}
    >
      <Button
        ref={triggerRef}
        isLoading={isSaving}
        isDisabled={isSaving}
        size="medium"
        className="pl-x0 pr-x1"
        variant={isPrimary ? 'secondary' : 'primary'}
        onClick={handleOnClick}
      >
        <div className="flex items-center gap-x0_25">
          <div className="flex items-center gap-x0_5 border-r border-solid pr-x1">
            <UserOutline />
            {primaryNarrativeCount || 0}
          </div>
          {renderButtonText()}
        </div>
      </Button>
    </FairValueTooltip>
  );
};
